import React, { useEffect, useState, useCallback } from 'react';
import { Plus, FileSignature, Trash2, Pencil, Users, Copy, Files, ArrowLeft } from 'lucide-react';
import * as SignService from '../service/sign';
import { updatedText, lastTouchedAt } from './timeText';

/**
 * The templates list — reusable starting points, board-wide.
 *
 * Reached from step 1's starters section, not from a top-level tab: a template
 * is a means to a signing request, and the app's front door is the request
 * itself.
 *
 * Creation lives in the shell rather than here, because step 1 offers the same
 * door on a board with no templates and both should land in the same editor.
 */
export default function TemplatesTab({ monday, getSessionToken, boardId, onEdit, onCreate, onBack }) {
  const [templates, setTemplates] = useState(null);
  const [creating, setCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const load = useCallback(async () => {
    if (!boardId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.listTemplates(token, boardId);
      setTemplates(res?.ok ? res.templates || [] : []);
    } catch (err) {
      console.error('Failed to load sign templates', err);
      setTemplates([]);
    }
  }, [boardId, getSessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await onCreate();
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (template) => {
    if (duplicatingId) return;
    setDuplicatingId(template.id);
    try {
      const token = await getSessionToken();
      const res = await SignService.duplicateTemplate(token, template.id);
      if (res?.ok) {
        await load();
        monday.execute('notice', { message: `Duplicated as "${res.template?.name}".`, type: 'success' });
      } else {
        monday.execute('notice', { message: 'Could not duplicate the template.', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to duplicate sign template', err);
      monday.execute('notice', { message: 'Could not duplicate the template.', type: 'error' });
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (template) => {
    const published = Number(template.version) > 0;
    const confirmed = await monday
      .execute('confirm', {
        message: published
          ? `Delete "${template.name}"? New requests will no longer be able to start from it. Requests already sent are not affected.`
          : `Delete the draft "${template.name}"?`,
        confirmButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .then((res) => res?.data?.confirm)
      .catch(() => window.confirm(`Delete "${template.name}"?`));
    if (!confirmed) return;

    try {
      const token = await getSessionToken();
      const res = await SignService.deleteTemplate(token, template.id);
      if (res?.ok) {
        setTemplates((prev) => (prev || []).filter((t) => t.id !== template.id));
        monday.execute('notice', { message: 'Template deleted.', type: 'success' });
      } else {
        monday.execute('notice', { message: 'Could not delete the template.', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to delete sign template', err);
      monday.execute('notice', { message: 'Could not delete the template.', type: 'error' });
    }
  };

  if (templates === null) {
    return <div className='py-16 text-center text-sm text-gray-500'>Loading templates…</div>;
  }

  return (
    <div className='max-w-2xl mx-auto px-4 py-5'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2 min-w-0'>
          {/* Back rather than "Done": nothing here is being confirmed — this is a
              detour off step 1, and the arrow says which way home is. */}
          {onBack && (
            <button
              onClick={onBack}
              title='Back to the request'
              className='p-1.5 -ml-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0'
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div className='min-w-0'>
            <h2 className='text-base font-semibold text-gray-900'>Templates</h2>
            <p className='text-xs text-gray-500 mt-0.5'>
              Reusable starting points, shared by every item on this board.
            </p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          disabled={creating || !boardId}
          className='flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0'
        >
          <Plus size={15} />
          New template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className='border border-dashed border-gray-300 rounded-lg py-14 text-center bg-white px-6'>
          <FileSignature size={28} className='mx-auto text-gray-300 mb-2' />
          <div className='text-sm font-medium text-gray-800'>No templates yet</div>
          <p className='text-sm text-gray-500 mt-1 max-w-sm mx-auto'>
            Build one once — every signing request on this board can start from it, with the
            documents, field placements and roles already in place.
          </p>
        </div>
      ) : (
        <ul className='space-y-2'>
          {templates.map((t) => (
            <li
              key={t.id}
              className='flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-gray-300 transition-colors'
            >
              <button onClick={() => onEdit(t.id)} className='flex-1 min-w-0 text-left'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium text-gray-900 truncate'>{t.name}</span>
                  {/*
                    "Active v1", not "v1" — the same chip the editor shows, and
                    for the same reason: the version alone answers "which
                    version" but not "can a request start from this yet?", which
                    is the only question this list is being scanned for. Paired
                    with "Draft" as its opposite it reads as a state rather than
                    a number.
                  */}
                  <span
                    className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                      Number(t.version) > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {Number(t.version) > 0 ? `Active v${t.version}` : 'Draft'}
                  </span>
                </div>
                <div className='flex items-center gap-3 text-xs text-gray-500 mt-0.5'>
                  <span className='flex items-center gap-1'>
                    <Files size={11} />
                    {t.documentCount} {t.documentCount === 1 ? 'document' : 'documents'}
                  </span>
                  <span className='flex items-center gap-1'>
                    <Users size={11} />
                    {t.roleCount}
                  </span>
                  {lastTouchedAt(t) && (
                    <span className='text-gray-400'>Updated {updatedText(lastTouchedAt(t))}</span>
                  )}
                </div>
              </button>
              <button
                onClick={() => onEdit(t.id)}
                title='Edit'
                className='p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100'
              >
                <Pencil size={15} />
              </button>
              <button
                onClick={() => handleDuplicate(t)}
                disabled={duplicatingId !== null}
                title='Duplicate — copies documents, fields and roles into a new draft'
                className='p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40'
              >
                <Copy size={15} className={duplicatingId === t.id ? 'animate-pulse' : ''} />
              </button>
              <button
                onClick={() => handleDelete(t)}
                title='Delete'
                className='p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50'
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
